import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaShieldAlt, FaLightbulb, FaUsers, FaHeart, FaRocket, FaStar, FaSatellite, FaBrain, FaEye, FaBell } from 'react-icons/fa';
import './About.css';

const About = () => {
  const { t } = useTranslation();

  const values = [
    { icon: FaShieldAlt, title: t('about.values.security'), desc: t('about.values.securityDesc') },
    { icon: FaLightbulb, title: t('about.values.simplicity'), desc: t('about.values.simplicityDesc') },
    { icon: FaRocket, title: t('about.values.innovation'), desc: t('about.values.innovationDesc') },
    { icon: FaHeart, title: t('about.values.support'), desc: t('about.values.supportDesc') }
  ];

  const stats = [
    { number: '10K+', label: 'Active Users' },
    { number: '50K+', label: 'Properties Managed' },
    { number: '15+', label: 'Countries' },
    { number: '98%', label: 'Satisfaction Rate' }
  ];

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero" data-aos="fade-up">
        <div className="container">
          <h1>{t('about.title')}</h1>
          <p className="about-subtitle">{t('about.subtitle')}</p>
        </div>
      </section>

      {/* Story Section */}
      <section className="about-section" data-aos="fade-up">
        <div className="container">
          <div className="content-grid">
            <div className="content-text">
              <h2>{t('about.story.title')}</h2>
              <p>{t('about.story.content')}</p>
            </div>
            <div className="content-image">
              <div className="image-placeholder">
                <span className="emoji">📱</span>
                <span className="emoji">🏡</span>
                <span className="emoji">📄</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="mission-vision" data-aos="fade-up">
        <div className="container">
          <div className="mv-grid">
            <div className="mv-card" data-aos="fade-right">
              <div className="mv-icon">🎯</div>
              <h3>{t('about.mission.title')}</h3>
              <p>{t('about.mission.content')}</p>
            </div>
            <div className="mv-card" data-aos="fade-left">
              <div className="mv-icon">🔮</div>
              <h3>{t('about.vision.title')}</h3>
              <p>{t('about.vision.content')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="about-stats" data-aos="fade-up">
        <div className="container">
          <h2>Our Impact</h2>
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div key={index} className="stat-card" data-aos="zoom-in" data-aos-delay={index * 100}>
                <h3 className="gradient-text">{stat.number}</h3>
                <p>{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="values-section" data-aos="fade-up">
        <div className="container">
          <div className="section-header">
            <h2>{t('about.values.title')}</h2>
            <p>The principles that guide everything we do</p>
          </div>
          <div className="values-grid">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="value-card" data-aos="fade-up" data-aos-delay={index * 100}>
                  <div className="value-icon">
                    <Icon />
                  </div>
                  <h3>{value.title}</h3>
                  <p>{value.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* AI Technology Section */}
      <section className="ai-technology-section" data-aos="fade-up">
        <div className="container">
          <div className="section-header">
            <div className="ai-badge-about">
              <span className="badge-pulse"></span>
              🤖 AI-POWERED TECHNOLOGY
            </div>
            <h2 className="gradient-text">{t('about.aiTechnology.title')}</h2>
            <p>{t('about.aiTechnology.subtitle')}</p>
          </div>
          
          <div className="ai-tech-grid">
            <div className="ai-tech-card" data-aos="fade-up" data-aos-delay="0">
              <div className="ai-tech-icon">
                <FaSatellite />
              </div>
              <h3>{t('about.aiTechnology.feature2')}</h3>
            </div>
            <div className="ai-tech-card" data-aos="fade-up" data-aos-delay="100">
              <div className="ai-tech-icon">
                <FaBrain />
              </div>
              <h3>{t('about.aiTechnology.feature1')}</h3>
            </div>
            <div className="ai-tech-card" data-aos="fade-up" data-aos-delay="200">
              <div className="ai-tech-icon">
                <FaEye />
              </div>
              <h3>{t('about.aiTechnology.feature4')}</h3>
            </div>
            <div className="ai-tech-card" data-aos="fade-up" data-aos-delay="300">
              <div className="ai-tech-icon">
                <FaBell />
              </div>
              <h3>{t('about.aiTechnology.feature3')}</h3>
            </div>
          </div>

          <div className="ai-description" data-aos="fade-up" data-aos-delay="400">
            <p>{t('about.aiTechnology.description')}</p>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="team-section" data-aos="fade-up">
        <div className="container">
          <div className="section-header">
            <h2>Meet Our Team</h2>
            <p>Passionate professionals dedicated to your success</p>
          </div>
          <div className="team-grid">
            {[
              { name: 'Rajesh Kumar', role: 'Founder & CEO', emoji: '👨‍💼' },
              { name: 'Priya Sharma', role: 'Head of Product', emoji: '👩‍💻' },
              { name: 'Amit Patel', role: 'Lead Developer', emoji: '👨‍💻' },
              { name: 'Sneha Singh', role: 'Customer Success', emoji: '👩‍💼' }
            ].map((member, index) => (
              <div key={index} className="team-card" data-aos="fade-up" data-aos-delay={index * 100}>
                <div className="team-avatar">{member.emoji}</div>
                <h3>{member.name}</h3>
                <p>{member.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section" data-aos="fade-up">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Experience AI-Powered Property Protection?</h2>
            <p>Join thousands of property owners using AI-Watchman to monitor their land</p>
            <button className="btn btn-primary btn-lg">
              Enable AI-Watchman Now
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
